// Local search manifest for the audit log, plus the translation from kore's
// filter object to the `AuditFilters` wire shape. Same pattern as
// surveys-filters.ts: the manifest drives the picker, the translation guards
// what a hand-edited URL can still produce.

import {
    type DurationOp,
    type EnumOp,
    type FilterDef,
    FilterOperator,
    type FilterResponse,
    type FilterValuesResponse,
    type IdOp,
    type TemporalOp,
} from "@pej/kore/components";
import { useCallback } from "react";
import {
    type AuditFilters,
    type AuditQueryField,
    type AuditSearchRq,
    type AuditSortField,
    fetchAuditFacetValues,
} from "@/api/audit";
import type { AuditAction, AuditGroup, AuditTargetType } from "@/api/types";
import { acceptedOp } from "@/features/search/wire-op";
import { AUDIT_ACTION_LABELS, auditTargetTypeLabel } from "./kore-audit-adapter";

const TARGET_TYPES: readonly AuditTargetType[] = [
    "Workspace",
    "Survey",
    "Placement",
    "DesignTemplate",
    "ContextKey",
    "Response",
];
const GROUPS: readonly AuditGroup[] = [
    "Workspaces",
    "Surveys",
    "Placements",
    "DesignTemplates",
    "Security",
    "Responses",
];
const GROUP_LABELS: Record<AuditGroup, string> = {
    Workspaces: "Workspaces",
    Surveys: "Surveys",
    Placements: "Placements",
    DesignTemplates: "Templates",
    Security: "Security",
    Responses: "Responses",
};
const ACTIONS: readonly AuditAction[] = [
    "Created",
    "Updated",
    "Deleted",
    "PlacementTokenRotated",
    "InvitesCreated",
    "RespondentsErased",
];

// Names and order follow vouchers' audit search contract.
const AUDIT_FILTER_DEFS: FilterDef[] = [
    {
        // `Next` omitted: events are never in the future.
        type: "Duration",
        filterParam: "occurredWithin",
        label: "When",
        operators: [FilterOperator.Last, FilterOperator.OlderThan],
    },
    {
        type: "Instant",
        filterParam: "occurredAt",
        label: "Occurred at",
        operators: [
            FilterOperator.Gt,
            FilterOperator.Gte,
            FilterOperator.Lt,
            FilterOperator.Lte,
            FilterOperator.Between,
        ],
    },
    {
        type: "Dynamic",
        filterParam: "actorIds",
        label: "Actor",
        operators: [FilterOperator.Eq],
        display: "List",
        valuesEndpoint: "audit/events/facet-values/actors",
    },
    {
        type: "Enum",
        filterParam: "actions",
        label: "Action",
        operators: [FilterOperator.Eq, FilterOperator.Neq],
        values: ACTIONS.map((value) => ({ value, label: AUDIT_ACTION_LABELS[value] })),
    },
    {
        type: "Enum",
        filterParam: "targetTypes",
        label: "Target type",
        operators: [FilterOperator.Eq, FilterOperator.Neq],
        values: TARGET_TYPES.map((value) => ({ value, label: auditTargetTypeLabel(value) })),
    },
    {
        type: "Dynamic",
        filterParam: "targetIds",
        label: "Target",
        operators: [FilterOperator.Eq],
        display: "List",
        valuesEndpoint: "audit/events/facet-values/targets",
    },
    {
        type: "Enum",
        filterParam: "groups",
        label: "Group",
        operators: [FilterOperator.Eq, FilterOperator.Neq],
        values: GROUPS.map((value) => ({ value, label: GROUP_LABELS[value] })),
    },
];

export const AUDIT_SEARCH_MANIFEST: FilterResponse = {
    filters: AUDIT_FILTER_DEFS,
    query: {
        fields: [
            { value: "ActorName", label: "Actor" },
            { value: "TargetName", label: "Target" },
        ],
    },
    sortBy: {
        fields: [{ field: "OccurredAt", label: "Time" }],
        default: { field: "OccurredAt", order: "Desc" },
    },
};

export const AUDIT_PAGE_SIZE_OPTIONS: readonly number[] = [25, 50, 100];
export const AUDIT_SORT_FIELDS: readonly AuditSortField[] = ["OccurredAt"];
export const AUDIT_QUERY_FIELDS: readonly AuditQueryField[] = ["ActorName", "TargetName"];
export const AUDIT_DEFAULT_ORDER: { field: AuditSortField; order: "Asc" | "Desc" } = {
    field: "OccurredAt",
    order: "Desc",
};

export const AUDIT_TIMELINE_PAGE_SIZE = 30;

/**
 * What a History trail covers: some targets, or a whole group. Targets always
 * carry their type, which leads kore's `audit_event_target_history` index; by
 * id alone the query scans the workspace.
 */
export type HistoryScope =
    | { targetTypes: readonly AuditTargetType[]; targetIds: readonly string[] }
    | { groups: readonly AuditGroup[] };

/** One builder for every timeline, so a one-target History and the sheet's timeline share a cache entry. */
export function historyRq(scope: HistoryScope): AuditSearchRq {
    const filters: AuditFilters =
        "groups" in scope
            ? { groups: { type: "Eq", values: [...scope.groups] } }
            : {
                  targetTypes: { type: "Eq", values: [...scope.targetTypes] },
                  targetIds: { type: "Eq", values: [...scope.targetIds] },
              };
    return { filters, orderBy: AUDIT_DEFAULT_ORDER, pageSize: AUDIT_TIMELINE_PAGE_SIZE };
}

/** One target's newest events: the sheet's timeline. */
export function targetTimelineRq(targetType: AuditTargetType, targetId: string): AuditSearchRq {
    return historyRq({ targetTypes: [targetType], targetIds: [targetId] });
}

/** The same scope as audit page URL filters, in kore's grammar: a bare key is Eq, values comma-separated. */
export function historySearch(scope: HistoryScope): { groups?: string; targetTypes?: string; targetIds?: string } {
    if ("groups" in scope) return { groups: scope.groups.join(",") };
    return { targetTypes: scope.targetTypes.join(","), targetIds: scope.targetIds.join(",") };
}

/** What `useKoreSearch` produces from the defs above: wider than `AuditFilters`. */
export interface AuditSearchBarFilters {
    groups?: EnumOp<string>;
    targetTypes?: EnumOp<string>;
    actions?: EnumOp<string>;
    actorIds?: EnumOp<string>;
    targetIds?: EnumOp<string>;
    occurredAt?: { type: string };
    occurredWithin?: { type: string };
}

const TEMPORAL_WIRE_OPS: readonly TemporalOp["type"][] = ["Eq", "Neq", "Lt", "Lte", "Gt", "Gte", "Between"];
const DURATION_WIRE_OPS: readonly DurationOp["type"][] = ["Last", "Next", "OlderThan"];

export function auditFiltersForWire(raw: AuditSearchBarFilters | undefined): AuditFilters {
    return {
        groups: knownValues(raw?.groups, GROUPS),
        targetTypes: knownValues(raw?.targetTypes, TARGET_TYPES),
        actions: knownValues(raw?.actions, ACTIONS),
        actorIds: idFilter(raw?.actorIds),
        targetIds: idFilter(raw?.targetIds),
        occurredAt: acceptedOp<TemporalOp>(raw?.occurredAt, TEMPORAL_WIRE_OPS),
        occurredWithin: acceptedOp<DurationOp>(raw?.occurredWithin, DURATION_WIRE_OPS),
    };
}

/** Drops values outside the closed enum; an all-unknown chip is dropped whole. */
function knownValues<E extends string>(op: EnumOp<string> | undefined, known: readonly E[]): EnumOp<E> | undefined {
    if (op === undefined) return undefined;
    const values = op.values.filter((value): value is E => (known as readonly string[]).includes(value));
    if (values.length === 0) return undefined;
    return { type: op.type, values };
}

/** `IdOp` is Eq-only; a `!`-negated URL chip would be a 400. */
function idFilter(op: EnumOp<string> | undefined): IdOp | undefined {
    if (op?.type !== "Eq" || op.values.length === 0) return undefined;
    return { type: "Eq", values: op.values };
}

/** `loadDynamicOptions` for `useKoreSearch`: resolves the def's `valuesEndpoint` under the workspace. */
export function useAuditFacetLoader(workspaceId: string) {
    return useCallback(
        (filterParam: string, q: string): Promise<FilterValuesResponse> => {
            const def = AUDIT_FILTER_DEFS.find((d) => d.filterParam === filterParam);
            if (def?.type !== "Dynamic") {
                return Promise.reject(new Error(`No Dynamic facet '${filterParam}' in the audit manifest`));
            }
            return fetchAuditFacetValues(workspaceId, def.valuesEndpoint, q);
        },
        [workspaceId],
    );
}
